/** The account-related menu rows shared by the AppBar's desktop
 *  profile menu (opened from the avatar) and the phone's "More
 *  actions" overflow menu — one component so the two can't drift
 *  apart (FW-37, GitLab issue #60).
 *
 *  Order: Account settings, Admin (only when the viewer can see it),
 *  What's new (with the unread dot/count), Help & keyboard shortcuts,
 *  Language, a divider, then Sign out. */
import {
  AccountCircle as AccountCircleIcon,
  HelpOutlineOutlined as HelpOutlineOutlinedIcon,
  Logout as LogoutIcon,
  NewReleasesOutlined as NewReleasesOutlinedIcon,
  TerminalOutlined as TerminalOutlinedIcon,
} from "@/components/icons";
import * as React from "react";
import { Badge, Divider, ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import { useTranslation } from "react-i18next";

import { LanguageMenuButton } from "@/components/language/LanguageMenuButton";

export interface AccountMenuItemsProps {
  /** Same gate as the rest of the app: ``admin_scope.can_access_admin``. */
  isAdmin: boolean;
  /** Unread "What's new" count — 0 hides the dot. */
  unread: number;
  /** Pre-built label ("What's new" or "What's new: N new changes"). */
  whatsNewLabel: string;
  /** Navigate to a route, then close the enclosing menu. */
  onNavigate: (path: string) => void;
  /** Open the keyboard-shortcuts dialog, then close the enclosing menu. */
  onHelp: () => void;
  /** Sign out, then close the enclosing menu. */
  onSignOut: () => void;
  /** The language picker closed (picked or dismissed): close the
   *  enclosing menu too, so it doesn't linger behind the dialog. */
  onLanguageDone: () => void;
}

export function AccountMenuItems({
  isAdmin,
  unread,
  whatsNewLabel,
  onNavigate,
  onHelp,
  onSignOut,
  onLanguageDone,
}: AccountMenuItemsProps): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <MenuItem onClick={() => onNavigate("/app/account")}>
        <ListItemIcon>
          <AccountCircleIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={t("appbar.account_settings", "Account settings")} />
      </MenuItem>
      {isAdmin && (
        <MenuItem onClick={() => onNavigate("/app/admin")}>
          <ListItemIcon>
            <TerminalOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t("appbar.admin")} />
        </MenuItem>
      )}
      <MenuItem onClick={() => onNavigate("/app/changelog")} aria-label={whatsNewLabel}>
        <ListItemIcon>
          <Badge variant="dot" color="success" invisible={unread === 0}>
            <NewReleasesOutlinedIcon fontSize="small" />
          </Badge>
        </ListItemIcon>
        <ListItemText primary={whatsNewLabel} />
      </MenuItem>
      <MenuItem onClick={onHelp}>
        <ListItemIcon>
          <HelpOutlineOutlinedIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={t("appbar.help", "Help & keyboard shortcuts")} />
      </MenuItem>
      {/* Opens the same dialog/popover picker as everywhere else; no
          menu-inside-a-menu. */}
      <LanguageMenuButton asMenuItem onDone={onLanguageDone} />
      <Divider />
      <MenuItem onClick={onSignOut}>
        <ListItemIcon>
          <LogoutIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={t("appbar.sign_out")} />
      </MenuItem>
    </>
  );
}
